import React, { useRef, useEffect, useState, memo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useLanguage } from '../contexts/LanguageProvider';

// Preload map tiles for better performance
if (typeof window !== 'undefined') {
  const preloadTiles = () => {
    // Preload Esri tiles for English labels around Ano Viannos area
    const tilesToPreload = [
      // Central Ano Viannos area tiles (z=11)
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/11/724/1166',
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/11/724/1165',
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/11/724/1167',
      // Surrounding tiles for smooth panning
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/11/723/1166',
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/11/725/1166',
      // Zoom level 10 for zoom out
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/10/362/583',
      // Zoom level 12 for zoom in
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/12/1448/2332',
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/12/1449/2332'
    ];

    tilesToPreload.forEach(url => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
    });
  };

  // Preload tiles immediately for faster map interaction
  setTimeout(preloadTiles, 500);
}

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons for different categories
const createCategoryIcon = (category) => {
  const colors = {
    beach: '#1e40af', // darker blue
    restaurant: '#c2410c', // darker orange
    historical_site: '#7c2d92', // darker purple
    nature: '#15803d', // darker green
    default: '#374151' // darker gray
  };

  const color = colors[category] || colors.default;
  const ariaLabel = getCategoryAriaLabel(category);

  return L.divIcon({
    className: 'custom-div-icon',
    html: `
      <div style="
        background-color: ${color};
        width: 30px;
        height: 30px;
        border-radius: 50%;
        border: 3px solid black;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: #ffffff !important;
        font-weight: bold;
        font-family: Arial, sans-serif;
        text-shadow: 1px 1px 1px rgba(0,0,0,0.5);
      " role="img" aria-label="${ariaLabel}">
        ${getCategorySymbol(category)}
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

const getCategorySymbol = (category) => {
  const symbols = {
    beach: 'B',
    restaurant: 'R',
    historical_site: 'H',
    nature: 'N',
    default: 'A'
  };
  return symbols[category] || symbols.default;
};

const getCategoryAriaLabel = (category) => {
  const labels = {
    beach: 'Beach attraction marker',
    restaurant: 'Restaurant marker',
    historical_site: 'Historical site marker',
    nature: 'Nature attraction marker',
    default: 'Attraction marker'
  };
  return labels[category] || labels.default;
};

const getCategoryColor = (category) => {
  const colors = {
    beach: "bg-blue-100 text-blue-800",
    restaurant: "bg-orange-100 text-orange-800",
    historical_site: "bg-purple-100 text-purple-800",
    nature: "bg-green-100 text-green-800"
  };
  return colors[category] || "bg-gray-100 text-gray-800";
};

// Component to handle map events and scrolling
function MapEventHandler({ onAttractionClick }) {
  const map = useMap();
  
  useEffect(() => {
    // Removed automatic jump to card on popup open
    // Now only "View Details" button will trigger the jump
    return () => {
      // Cleanup if needed
    };
  }, [map, onAttractionClick]);

  return null;
}

const InteractiveMap = memo(function InteractiveMap({ 
  attractions = [], 
  onAttractionClick,
  getAttractionTranslation,
  className = "",
  height = "500px" 
}) {
  const { t } = useLanguage();
  const [mapCenter] = useState([35.055, 25.423]); // Ano Viannos coordinates
  const [mapZoom] = useState(11);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const mapRef = useRef();
  const containerRef = useRef();

  console.log('InteractiveMap received attractions:', attractions);

  // Filter attractions that have coordinates
  const mappableAttractions = attractions.filter(
    attraction => attraction.latitude && attraction.longitude
  );

  console.log('Mappable attractions:', mappableAttractions);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: '100px', // Start loading when 100px away from viewport
        threshold: 0.1 
      }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Handle map load event
  const handleMapLoad = () => {
    setMapLoaded(true);
  };

  const handleViewDetails = (attractionId) => {
    if (onAttractionClick) {
      onAttractionClick(attractionId);
    }
  };

  // Universal map URL generator - works with any map app
  const generateMapUrl = (attraction) => {
    if (!attraction.latitude || !attraction.longitude) return null;
    
    const lat = attraction.latitude;
    const lng = attraction.longitude;
    const name = encodeURIComponent(attraction.name || 'Location');
    
    // Universal geo: URL that works on all platforms
    return `geo:${lat},${lng}?q=${lat},${lng}(${name})`;
  };

  // Handle directions click with universal map support
  const handleDirectionsClick = (attraction) => {
    const geoUrl = generateMapUrl(attraction);
    if (!geoUrl) return;

    // Try geo: URL first
    const link = document.createElement('a');
    link.href = geoUrl;
    link.click();

    // Fallback to Google Maps web if geo: doesn't work
    setTimeout(() => {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${attraction.latitude},${attraction.longitude}`;
      window.open(googleMapsUrl, '_blank');
    }, 1000);
  };

  // Loading placeholder before attractions load
  if (mappableAttractions.length === 0) {
    return (
      <div 
        ref={containerRef}
        className={`bg-gray-100 rounded-lg p-8 text-center ${className}`} 
        style={{ height }}
      >
        <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-600 mb-2">Loading Attractions...</h3>
        <p className="text-gray-500">Map will appear once attraction data is loaded.</p>
        <div className="mt-4">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Show placeholder until map comes into view
  if (!isInView) {
    return (
      <div 
        ref={containerRef}
        className={`bg-gradient-to-br from-blue-50 to-green-50 rounded-lg p-8 text-center ${className}`} 
        style={{ height }}
      >
        <MapPin className="w-16 h-16 text-blue-500 mx-auto mb-4" />
        <h3 className="text-2xl font-semibold text-gray-700 mb-2">Interactive Map</h3>
        <p className="text-gray-600 mb-4">
          Explore {mappableAttractions.length} attractions around Ano Viannos
        </p>
        <div className="grid grid-cols-2 gap-4 max-w-md mx-auto text-sm">
          <div className="flex items-center gap-2 text-blue-600">
            <div className="w-5 h-5 rounded-full bg-blue-800 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Beach marker">B</div>
            <span>Beaches</span>
          </div>
          <div className="flex items-center gap-2 text-orange-600">
            <div className="w-5 h-5 rounded-full bg-orange-700 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Restaurant marker">R</div>
            <span>Restaurants</span>
          </div>
          <div className="flex items-center gap-2 text-purple-600">
            <div className="w-5 h-5 rounded-full bg-purple-700 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Historical site marker">H</div>
            <span>Historical Sites</span>
          </div>
          <div className="flex items-center gap-2 text-green-600">
            <div className="w-5 h-5 rounded-full bg-green-700 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Nature marker">N</div>
            <span>Nature & Hiking</span>
          </div>
        </div>
      </div>
    );
  }

  // Error boundary for map rendering
  try {
    return (
      <div className={`relative rounded-lg overflow-hidden shadow-lg ${className}`}>
        {/* Loading overlay */}
        {!mapLoaded && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-30">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Loading Map...</h3>
              <p className="text-gray-500">Preparing your interactive experience</p>
            </div>
          </div>
        )}
        
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height, width: '100%' }}
          ref={mapRef}
          className="z-10"
          whenReady={handleMapLoad}
          preferCanvas={true}
          zoomControl={true}
          doubleClickZoom={true}
          closePopupOnClick={true}
          dragging={true}
          zoomSnap={1}
          zoomDelta={1}
          trackResize={true}
          touchZoom={true}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='© Esri'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
            maxZoom={19}
            tileSize={256}
            keepBuffer={4}
            updateWhenZooming={false}
            updateWhenIdle={true}
          />
          
          <MapEventHandler onAttractionClick={onAttractionClick} />
          
          {mappableAttractions.map((attraction) => (
            <Marker
              key={attraction.id}
              position={[parseFloat(attraction.latitude), parseFloat(attraction.longitude)]}
              icon={createCategoryIcon(attraction.category)}
            >
              {/* Tooltip - shows on hover */}
              <Tooltip 
                direction="top" 
                offset={[0, -20]}
                opacity={0.9}
                permanent={false}
              >
                <div className="text-sm font-semibold">
                  {getAttractionTranslation ? getAttractionTranslation(attraction, 'name') : attraction.name}
                </div>
              </Tooltip>

              {/* Popup - shows on click */}
              <Popup
                attractionId={attraction.id}
                maxWidth={300}
                className="custom-popup"
              >
                <div className="p-2">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 mb-1">
                      {getAttractionTranslation ? getAttractionTranslation(attraction, 'name') : attraction.name}
                    </h3>
                      <div className="flex gap-2 mb-2">
                        <Badge className={getCategoryColor(attraction.category)}>
                          {attraction.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                        <Badge variant="outline" className="text-blue-600">
                          {attraction.distance_km} km
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                    {getAttractionTranslation ? getAttractionTranslation(attraction, 'description') : attraction.description}
                  </p>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleViewDetails(attraction.id)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <Star className="w-3 h-3 mr-1" />
                      View Details
                    </Button>
                    
                    {attraction.latitude && attraction.longitude && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDirectionsClick(attraction)}
                        className="flex-1"
                      >
                        <Navigation className="w-3 h-3 mr-1" />
                        Directions
                      </Button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        
        {/* Map Legend */}
        <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-20">
          <h4 className="font-semibold text-sm text-gray-700 mb-2">{t('mapLegend')}</h4>
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-blue-800 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Beach category marker">B</div>
              <span>{t('categoryBeaches')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-700 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Restaurant category marker">R</div>
              <span>{t('categoryDining')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-purple-700 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Historical site category marker">H</div>
              <span>{t('categoryHistorical')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-700 flex items-center justify-center text-white text-xs font-bold" role="img" aria-label="Nature category marker">N</div>
              <span>{t('categoryNature')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Map rendering error:', error);
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-8 text-center ${className}`} style={{ height }}>
        <MapPin className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-red-700 mb-2">Map Error</h3>
        <p className="text-red-600 mb-4">Unable to load interactive map</p>
        <p className="text-sm text-red-500">Check console for details</p>
      </div>
    );
  }
});

export default InteractiveMap;
